import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getAccountDeletion, DeletionRequestDetail } from '../../services/accountDeletionAdminService';
import { ArrowBackIcon, RefreshIcon } from '../../components/common/Icons';
import {
  APPROVAL_LABELS,
  EXECUTION_LABELS,
  HOLD_LABELS,
  LEGACY_LABELS,
  OUTSTANDING_LABELS,
  READINESS_LABELS,
  label,
} from './accountDeletionLabels';
import { formatDate, ReadOnlyNotice, StatusBadge } from './AccountDeletionsPage';
import '../../styles/AuditTrailsPage.css';
import '../../styles/AccountDeletionsPage.css';

const errorMessage = (err: any): string => {
  const status = err?.response?.status;
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 403) return 'You do not have access to account deletions.';
  if (status === 404) return 'This deletion request was not found.';
  if (status === 400) return 'This is not a valid deletion request id.';
  return 'This deletion request could not be loaded.';
};

const Milestone: React.FC<{
  done: boolean;
  title: string;
  when?: string | null;
  doneNote?: string;
  note?: string;
}> = ({ done, title, when, doneNote, note }) => (
  <li className={`account-deletions__milestone${done ? ' account-deletions__milestone--done' : ''}`}>
    <span className="account-deletions__milestone-mark" aria-hidden="true">
      {done ? '✓' : '○'}
    </span>
    <span>
      <strong>{title}</strong>
      <span className="account-deletions__muted"> {done ? doneNote || formatDate(when) : note || 'Not yet'}</span>
    </span>
  </li>
);

const Codes: React.FC<{ codes: string[]; labels: Record<string, string>; empty: string; tone?: string }> = ({
  codes,
  labels,
  empty,
  tone,
}) =>
  codes.length ? (
    <ul className="account-deletions__chips">
      {codes.map((code) => (
        <li key={code} className={`account-deletions__chip${tone ? ` account-deletions__chip--${tone}` : ''}`}>
          {label(labels, code)}
        </li>
      ))}
    </ul>
  ) : (
    <span className="account-deletions__muted">{empty}</span>
  );

const AccountDeletionDetailPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<DeletionRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestSeq = useRef(0);

  // keepDetail: only a manual refresh of the same request keeps it on screen.
  const load = useCallback(
    async (keepDetail = false) => {
      const seq = ++requestSeq.current;
      setLoading(true);
      setError(null);
      if (!keepDetail) setDetail(null);
      try {
        const data = await getAccountDeletion(id);
        if (seq === requestSeq.current) setDetail(data);
      } catch (err) {
        if (seq === requestSeq.current) {
          setDetail(null);
          setError(errorMessage(err));
        }
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    },
    [id],
  );

  // A new id clears the previous request at once; a late answer for an
  // earlier id is ignored.
  useEffect(() => {
    load();
  }, [load]);

  const back = (
    <Link to="/admin/account-deletions" className="account-deletions__back">
      <ArrowBackIcon size={18} />
      All deletion requests
    </Link>
  );

  // A different id than the one shown: the previous request is never rendered.
  const stale = Boolean(detail && detail.id !== id);
  if ((loading && !detail) || (stale && !error)) {
    return (
      <div className="audit-trails account-deletions">
        {back}
        <div className="audit-trails__loading" role="status">
          Loading deletion request…
        </div>
      </div>
    );
  }
  if (error || !detail || stale) {
    return (
      <div className="audit-trails account-deletions">
        {back}
        <div className="account-deletions__error" role="alert">
          <p>{error || 'This deletion request could not be loaded.'}</p>
          <button type="button" className="audit-trails__page-btn" onClick={() => load()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  const d = detail;
  const completed = d.status === 'completed';
  const auditLink = `/admin/audit-trails?userId=${encodeURIComponent(d.userId)}`;

  return (
    <div className="audit-trails account-deletions">
      {back}
      <header className="audit-trails__header">
        <div>
          <h1 className="audit-trails__title">Deletion request</h1>
          <p className="audit-trails__subtitle">
            <code>{d.id}</code>
          </p>
        </div>
        <button type="button" className="audit-trails__refresh-btn" onClick={() => load(true)} disabled={loading}>
          <RefreshIcon />
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </header>

      <ReadOnlyNotice />

      <div className="account-deletions__grid">
        <section className="account-deletions__card" aria-labelledby="ad-summary">
          <h2 id="ad-summary">Summary</h2>
          <dl className="account-deletions__facts">
            <dt>Status</dt>
            <dd>
              <StatusBadge status={d.displayStatus} />
            </dd>
            <dt>User id</dt>
            <dd>
              <code>{d.userId}</code>
            </dd>
            <dt>Account type</dt>
            <dd>{d.userType || '—'}</dd>
            <dt>Recorded state</dt>
            <dd>
              <code>{d.status}</code>
            </dd>
            <dt>Last updated</dt>
            <dd>{formatDate(d.updatedAt)}</dd>
          </dl>
        </section>

        <section className="account-deletions__card" aria-labelledby="ad-lifecycle">
          <h2 id="ad-lifecycle">Lifecycle</h2>
          <ol className="account-deletions__milestones">
            <Milestone done={d.milestones.requestReceived} title="Request received" when={d.timeline.requestedAt} />
            {d.status === 'cancelled' ? (
              <Milestone done title="Cancelled by request" when={d.timeline.cancelledAt} />
            ) : (
              <>
                <li className="account-deletions__milestone">
                  <span className="account-deletions__milestone-mark" aria-hidden="true">
                    ·
                  </span>
                  <span>
                    <strong>Cancellation deadline</strong>
                    <span className="account-deletions__muted"> {formatDate(d.timeline.cancellationDeadline)}</span>
                  </span>
                </li>
                <Milestone done={d.milestones.requestApproved} title="Request approved" when={d.timeline.approvedAt} />
                <Milestone done={d.milestones.deletionExecuted} title="Deletion executed" when={d.timeline.executedAt} />
                <Milestone
                  done={d.milestones.deletionFullyVerified}
                  title="Deletion fully verified"
                  doneNote="Nothing outstanding (no verification time is recorded)"
                  note={completed ? 'Outstanding items remain' : 'Not yet'}
                />
              </>
            )}
          </ol>
          {d.process.completionTargetDays ? (
            <p className="account-deletions__muted">
              Approved completion target: {d.process.completionTargetDays} days after the cancellation window.
            </p>
          ) : (
            <p className="account-deletions__muted">No completion deadline is stated: the target is not approved.</p>
          )}
        </section>

        <section className="account-deletions__card" aria-labelledby="ad-holds">
          <h2 id="ad-holds">Holds</h2>
          <h3>Recorded at the last run</h3>
          <Codes codes={d.holds.recorded} labels={HOLD_LABELS} empty="None recorded" tone="hold" />
          <h3>Current local checks</h3>
          {d.holds.liveCheck === 'checked' ? (
            <Codes codes={d.holds.live} labels={HOLD_LABELS} empty="None found" tone="hold" />
          ) : d.holds.liveCheck === 'not_applicable' ? (
            <span className="account-deletions__muted">Not checked (request closed)</span>
          ) : (
            <span className="account-deletions__status account-deletions__status--warning" role="status">
              Hold verification unavailable
            </span>
          )}
          <p className="account-deletions__muted">
            Live Stripe checks (Connect balance, payouts, disputes, subscriptions) run only in the operator dry run.
          </p>
          {d.holds.review && (
            <p>
              Hold review: tier {d.holds.review.tier} ({d.holds.review.reviewer}), next due{' '}
              {formatDate(d.holds.review.nextDueAt)}
              {d.holds.review.overdue ? ' – overdue' : ''}
            </p>
          )}
        </section>

        <section className="account-deletions__card" aria-labelledby="ad-readiness">
          <h2 id="ad-readiness">Execution readiness</h2>
          {!d.readiness.applicable ? (
            <span className="account-deletions__muted">Not applicable ({completed ? 'executed' : 'request closed'})</span>
          ) : (
            <>
              <dl className="account-deletions__facts">
                <dt>Local checks</dt>
                <dd>
                  {d.readiness.localChecksClear ? (
                    <span className="account-deletions__status account-deletions__status--info">Clear</span>
                  ) : (
                    <span className="account-deletions__status account-deletions__status--neutral">Not clear</span>
                  )}
                </dd>
                <dt>Production deletion policy</dt>
                <dd>{d.process.productionPolicyActive ? 'Approved' : 'Inactive'}</dd>
                <dt>Emergency stop</dt>
                <dd>{d.process.emergencyStop === true ? 'Active' : d.process.emergencyStop === false ? 'Off' : 'Unknown'}</dd>
                <dt>Stripe / provider checks</dt>
                <dd>Not performed here</dd>
                <dt>Operator eligibility</dt>
                <dd>Decided only by the operator’s own gate and dry run</dd>
              </dl>
              {d.readiness.reasons.length > 0 && (
                <>
                  <h3>Why local checks are not clear</h3>
                  <Codes codes={d.readiness.reasons} labels={READINESS_LABELS} empty="" />
                </>
              )}
            </>
          )}
          <dl className="account-deletions__facts">
            <dt>Execution</dt>
            <dd>
              {label(EXECUTION_LABELS, d.execution.state)} ({d.execution.stepsCompleted}/{d.execution.stepsTotal} steps)
            </dd>
            <dt>Attempts</dt>
            <dd>{d.execution.attempts ?? 0}</dd>
            {d.execution.reviewReason && (
              <>
                <dt>Review reason</dt>
                <dd>
                  <code>{d.execution.reviewReason}</code>
                </dd>
              </>
            )}
          </dl>
        </section>

        <section className="account-deletions__card" aria-labelledby="ad-completion">
          <h2 id="ad-completion">Completion verification</h2>
          {!completed ? (
            <span className="account-deletions__muted">Not executed yet</span>
          ) : (
            <>
              <p>
                {d.completion.fullyComplete ? (
                  <span className="account-deletions__status account-deletions__status--success">Fully verified</span>
                ) : (
                  <span className="account-deletions__status account-deletions__status--warning">Not fully verified</span>
                )}
              </p>
              <Codes codes={d.completion.outstanding} labels={OUTSTANDING_LABELS} empty="Nothing outstanding" />
            </>
          )}
          <dl className="account-deletions__facts">
            <dt>Media cleanup</dt>
            <dd>
              {d.completion.mediaCleanupComplete
                ? 'Complete'
                : `${d.completion.mediaPending} pending, ${d.completion.mediaAbandoned} not removable, ${d.completion.mediaUnmanaged} outside managed cloud`}
            </dd>
            <dt>Legacy Cloudinary originals</dt>
            <dd>{label(LEGACY_LABELS, d.completion.legacyOriginals)}</dd>
            <dt>Stripe Connect closure</dt>
            <dd>
              <code>{d.completion.stripeConnectClosure}</code>
            </dd>
            <dt>Notification check</dt>
            <dd>
              <code>{d.completion.residualNotificationReview}</code>
            </dd>
          </dl>
        </section>

        <section className="account-deletions__card" aria-labelledby="ad-approvals">
          <h2 id="ad-approvals">Approvals</h2>
          {d.approvals.length === 0 ? (
            <span className="account-deletions__muted">No approval recorded</span>
          ) : (
            <ul className="account-deletions__list">
              {d.approvals.map((approval, index) => (
                <li key={`${approval.createdAt}-${index}`}>
                  {label(APPROVAL_LABELS, approval.state)}
                  <span className="account-deletions__muted"> · recorded {formatDate(approval.createdAt)}</span>
                  {approval.usedAt && <span className="account-deletions__muted"> · used {formatDate(approval.usedAt)}</span>}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="account-deletions__card account-deletions__card--wide" aria-labelledby="ad-audit">
          <h2 id="ad-audit">Audit events</h2>
          {d.auditEvents.length === 0 ? (
            <span className="account-deletions__muted">No deletion audit events for this account</span>
          ) : (
            <ul className="account-deletions__list">
              {d.auditEvents.map((event, index) => (
                <li key={`${event.action}-${event.at}-${index}`}>
                  <code className="audit-trails__action">{event.action}</code>
                  <span className="account-deletions__muted"> {formatDate(event.at)}</span>
                </li>
              ))}
            </ul>
          )}
          <Link to={auditLink} className="account-deletions__link">
            Open this account’s events in Audit Trails
          </Link>
        </section>
      </div>
    </div>
  );
};

export default AccountDeletionDetailPage;
